import { Inject, Injectable, OnDestroy } from '@angular/core';

import { filter, } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';

import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';

import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';

import { BriefingsService } from './briefings.service';
import { CuratedAutoWebsocketDialogComponent } from '../components/CuratedAutoWebsocketDialog/curated-auto-websocket-dialog.component';
import { MonitoringRoutingService } from '@Monitor/Core/services/monitoring-routing.service';

export class AutoGenerateInsightResponse {
    resourceId: {
        briefing: string;
    };
    stats: {
        insightsCompleted: number;
        totalInsightsToComplete: number;
        notEnoughContentMentions: number;
        failedToGenerateInsightMentions: number;
    };
    count: number;
    showDialog: boolean;
    loading: boolean = true;
}

@Injectable()
export class BriefingsAutoGenService implements OnDestroy {

    autoGensStatus: {
        [key: number]: AutoGenerateInsightResponse;
    } = {};
    private autoGensStatus$: BehaviorSubject<{[key: number]: AutoGenerateInsightResponse}>;
    briefingId: number;

    protected subscriptions: Subscription[] = [];

    constructor(
        @Inject('BriefingsService') public briefingsService: AbstractPageBuilderService,
        private websocketEvents: WebsocketAwareNeptuneService,
        private routingService: MonitoringRoutingService,
        public coreDialogService: CoreDialogService,
    ) {
        this.autoGensStatus$ = new BehaviorSubject<{[key: number]: AutoGenerateInsightResponse}>({});
        this.subscriptions.push(
            this.routingService.getCurrentRoute().subscribe(
                routeModel => {
                    this.briefingId = Number(routeModel.value);
                }
            ),
            this.websocketEvents.getWebsocketMessages().pipe(filter(x => !!x)).subscribe(message => {
                if (
                    message.resourceType === ResourceType.autogenerateinsights &&
                    message.slug && (message.slug as string).includes('autogenerateinsightstatus')
                ) {
                    if (message.autoGenerateInsightResponse?.resourceId?.briefing) {
                        const briefingId = Number(message.autoGenerateInsightResponse?.resourceId?.briefing);
                        const blockReferenceId = message.autoGenerateInsightResponse?.resourceId?.blockReferenceId;
                        let showDialog = false;
                        let loading = true;

                        if (this.autoGensStatus[briefingId]) {
                            showDialog = this.autoGensStatus[briefingId].showDialog;
                            loading = this.autoGensStatus[briefingId].loading;
                        }

                        if (briefingId && message.autoGenerateInsightResponse.mentionAutoInsights?.length) {
                            for (const mention of message.autoGenerateInsightResponse.mentionAutoInsights) {
                                mention.blockReferenceId = blockReferenceId;
                                mention.mentionId = mention.mercuryId;

                                (this.briefingsService as BriefingsService).cacheMentionWithAutoGenInsight(mention, briefingId);
                            }
                        }

                        // eslint-disable-next-line max-len
                        this.autoGensStatus[briefingId] = message.autoGenerateInsightResponse as AutoGenerateInsightResponse;
                        this.autoGensStatus[briefingId].showDialog = showDialog;
                        this.autoGensStatus[briefingId].loading = loading;

                        const autoGenResponse = message.autoGenerateInsightResponse as AutoGenerateInsightResponse;
                        if (autoGenResponse.stats) {
                            const successfulGenerations = autoGenResponse.stats.insightsCompleted || 0;
                            const notEnoughContentGenerations = autoGenResponse.stats.notEnoughContentMentions || 0;
                            const failedGenerations = autoGenResponse.stats.failedToGenerateInsightMentions || 0;
                            const totalInsightsToComplete = autoGenResponse.stats.totalInsightsToComplete || 0;

                            this.autoGensStatus[briefingId].count = successfulGenerations + notEnoughContentGenerations + failedGenerations;

                            if (this.autoGensStatus[briefingId].count >= totalInsightsToComplete) {
                                this.autoGensStatus[briefingId].loading = false;
                                if (this.briefingId === briefingId) {
                                    (this.briefingsService as BriefingsService).refreshCuratedMentions();
                                }

                                if (!this.autoGensStatus[briefingId].showDialog && (this.briefingId === briefingId)) {
                                    const dialogIO = {
                                        inputs: {
                                            briefingId
                                        },
                                        outputs: {}
                                    };
                                    this.coreDialogService.open(CuratedAutoWebsocketDialogComponent, dialogIO);
                                }
                            } else {
                                this.autoGensStatus[briefingId].loading = true;
                            }
                        }
                        this.setAutoGenStatus();
                    }
                }
            })
        );
    }

    closeAutoGenStatus(briefingId: number) {
        delete this.autoGensStatus[briefingId];
        this.setAutoGenStatus();
    }

    private setAutoGenStatus() {
        this.autoGensStatus$.next(this.autoGensStatus);
    }

    setSpecificAutoGenStatus(briefingId: number, autGenStatus: AutoGenerateInsightResponse) {
        this.autoGensStatus[briefingId] = autGenStatus;
        this.setAutoGenStatus();
    }

    getAutoGenStatus(): Observable<{[key: number]: AutoGenerateInsightResponse}> {
        return this.autoGensStatus$;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
